// Blip's tools (docs/BLIP_HELPER_DESIGN.md, "Tools"), carried out in the
// page: Anthropic's text editor tool over the open project's files, the
// IDE's compiler, a test run in the program pane, and the help; the World
// Editor's (world.js); models, pictures, sounds and music made for him
// (models.js); icons he draws himself (icons.js); and TODO notes (todo.js).
// Their definitions, as Claude sees them, are in web/ide/blip/tools.json.
//
// Blip edits only the open project's text files, through the editor, so
// that Ctrl+Z undoes each edit; he cannot delete or rename files. He can make
// a program the main file, the one Run starts, and compile and run another
// by name. Each request's changes, the main file's too, are recorded
// (begin), for the change card and Undo. The models, pictures, sounds and
// music he has made are new files, which never replace one, and have cards
// of their own.

import { normalisePath, splitPath } from "../projects.js";
import { versioned } from "../site.js";
import { ICON_SIZE, IconError, drawIcon, iconShapes, iconView, isBlipIcon } from "./icons.js";
import { ModelToolError, ModelTools, freePath } from "./models.js";
import { TodoError, TodoNotes } from "./todo.js";
import { WorldToolError, WorldTools } from "./world.js";

// The tools of models.js. Those that wait for Tripo, OpenAI, ElevenLabs or
// the shared library run at the same time when Claude asks for several at
// once.
const MODEL_TOOLS = new Set(["make_model", "change_model", "make_picture", "make_sound", "make_music", "offer_choices",
  "find_assets", "add_asset", "keep_picture"]);
const AT_ONCE = new Set(["make_model", "change_model", "make_picture", "make_sound", "make_music", "find_assets", "add_asset"]);
// While he writes a TODO note (todo.js), only the tools that look, besides
// save_todo; and the editor tool only views.
const TODO_TOOLS = new Set(["save_todo", "lookup_command", "search_help", "read_help", "world_state", "world_entity",
  "world_asset", "world_probe", "world_route", "world_look"]);
const TODO_REFUSED = "Not now: this message is a TODO note. Write it up with save_todo and change nothing; the user can " +
  "ask for it to be done later.";

const VIEW_CHARACTERS = 40000;
const FILE_BYTES = 1 << 20;
const PAGE_CHARACTERS = 12000;
// The parts after a page's cut that read_help names, at most.
const HELP_PARTS = 8;
const COMMAND_PAGE_CHARACTERS = 8000;
const OUTPUT_LINES = 200;
// The online test's networks, as Program > Run online test names them.
const NETWORKS = ["none", "good", "fair", "poor", "bad"];
const OUTPUT_CHARACTERS = 6000;
const CAPTURE_WIDTH = 640;
// A picture whose PNG is larger than this goes as a JPEG when that is
// smaller (smallPicture).
const PNG_BYTES = 160 << 10;

const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;
const lines = (text) => text.split("\n");

// A help page as text: headings, paragraphs and code kept apart. Given an
// id (from), the text starts at the heading, paragraph or list item that
// holds that element, such as a module page's #reference; null if the page
// has no element with that id.
export function htmlText(html, from = "") {
  const document = new DOMParser().parseFromString(html, "text/html");
  for (const element of document.querySelectorAll("script, style, nav")) element.remove();
  let root = document.body || document.documentElement;
  if (from) {
    const target = document.getElementById(from) ||
      [...document.querySelectorAll("[id]")].find((element) => element.id.toLowerCase() === from.toLowerCase());
    if (!target) return null;
    const start = target.closest("h1, h2, h3, h4, h5, h6, p, li, pre, dt, dd, tr, blockquote") || target;
    const range = document.createRange();
    range.setStartBefore(start);
    range.setEndAfter(root.lastChild);
    root = document.createElement("div");
    root.append(range.cloneContents());
  }
  const BLOCKS = new Set(["p", "div", "tr", "table", "section", "ul", "ol", "dl", "dt", "dd", "blockquote", "header",
    "footer", "article", "main", "figure", "figcaption", "details", "summary"]);
  const walk = (node) => {
    if (node.nodeType === 3) return node.nodeValue.replace(/\s+/g, " ");
    if (node.nodeType !== 1) return "";
    const tag = node.nodeName.toLowerCase();
    if (tag === "br") return "\n";
    if (tag === "pre") return `\n${node.textContent.replace(/\r\n/g, "\n").replace(/\s+$/, "")}\n`;
    const inner = [...node.childNodes].map(walk).join("");
    if (/^h[1-6]$/.test(tag)) return `\n\n${"#".repeat(Number(tag[1]))} ${inner.trim()}\n`;
    if (tag === "li") return `\n- ${inner.trim()}`;
    if (tag === "td" || tag === "th") return `${inner.trim()} `;
    if (BLOCKS.has(tag)) return `\n${inner}\n`;
    return inner;
  };
  return walk(root).split("\n").map((line) => line.trimEnd())
    .join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

// A page's parts, which read_help reads from: its second- and third-level
// headings that have ids, as { id, heading } with the heading as htmlText
// writes it.
export function helpParts(html) {
  const document = new DOMParser().parseFromString(html, "text/html");
  return [...document.querySelectorAll("h2[id], h3[id]")].map((element) => ({ id: element.id,
    heading: `${"#".repeat(Number(element.nodeName[1]))} ${element.textContent.replace(/\s+/g, " ").trim()}` }));
}

const cut = (text, limit, note) => text.length <= limit ? text : `${text.slice(0, limit)}\n\n(${note})`;

// A command's parameters, as the knowledge pack lists them.
export function commandSignature(command) {
  const params = command.params.map((param) =>
    `${param.name}${param.sigil}${param.default !== undefined && param.default !== "" ? `=${param.default}` : ""}`);
  if (command.returns) return `${command.name}${command.returns}(${params.join(", ")})`;
  return params.length ? `${command.name} ${params.join(", ")}` : command.name;
}

// The rows (from 1) of after that are not in before: a line diff of the
// stretch between their common first and last lines, by their longest
// common subsequence. A stretch too long for that is marked whole. A
// deletion marks the line after it.
export function changedRows(before, after) {
  const a = lines(before ?? ""), b = lines(after);
  if (before === null || before === undefined) return b.map((_, index) => index + 1);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) ++start;
  let end = 0;
  while (end < a.length - start && end < b.length - start && a[a.length - 1 - end] === b[b.length - 1 - end]) ++end;
  const oldLines = a.slice(start, a.length - end), newLines = b.slice(start, b.length - end);
  const rows = [];
  if (!newLines.length) {
    if (oldLines.length) rows.push(Math.min(start + 1, b.length));
    return rows;
  }
  if (oldLines.length * newLines.length > 4e6) return newLines.map((_, index) => start + index + 1);
  // lengths[i][j]: the longest common subsequence of oldLines from i and
  // newLines from j.
  const width = newLines.length + 1;
  const lengths = new Uint32Array((oldLines.length + 1) * width);
  for (let i = oldLines.length - 1; i >= 0; --i) {
    for (let j = newLines.length - 1; j >= 0; --j) {
      lengths[i * width + j] = oldLines[i] === newLines[j] ? lengths[(i + 1) * width + j + 1] + 1 :
        Math.max(lengths[(i + 1) * width + j], lengths[i * width + j + 1]);
    }
  }
  let i = 0, j = 0, deleted = false;
  while (j < newLines.length) {
    if (i < oldLines.length && oldLines[i] === newLines[j]) {
      if (deleted) rows.push(start + j + 1);
      deleted = false;
      ++i;
      ++j;
    } else if (i < oldLines.length && lengths[(i + 1) * width + j] >= lengths[i * width + j + 1]) {
      deleted = true;
      ++i;
    } else {
      rows.push(start + j + 1);
      deleted = false;
      ++j;
    }
  }
  if (deleted || i < oldLines.length) rows.push(Math.min(start + newLines.length + 1, b.length));
  return [...new Set(rows)];
}

// Numbered lines, as a terminal's cat -n shows them.
const numbered = (text, first = 1) => lines(text).map((line, index) => `${String(first + index).padStart(6)}\t${line}`)
  .join("\n");

export class BlipTools {
  // ide: { project(), editor, isText(path), projectPath(file) (the project's
  // spelling of a file a program names), refreshFiles(), compile(file),
  // checkRun({ frames, keys, signal, file }), setMain(path), help, commands,
  // examples() }
  constructor(ide) {
    this.ide = ide;
    this.searchIndex = null;
    // The world tools (world.js), which also scale pictures down.
    this.world = new WorldTools(Object.assign(Object.create(ide), { smallPicture }));
    this.models = new ModelTools(ide);
    this.notes = new TodoNotes(ide);
    this.begin();
  }

  // Whether a call may run at the same time as the calls beside it.
  parallel(call) {
    return AT_ONCE.has(call.name);
  }

  // A new request: no changes yet, and nothing seen. todo: whether its
  // message is a TODO note, which only looks; words and pictures: its own
  // (todo.js TodoNotes.begin).
  begin({ todo = false, words = "", pictures = [] } = {}) {
    this.changes = new Map();       // path -> { path, before (null for a file made), after }
    this.main = null;               // the main file he set: { before, after }
    this.seen = new Map();          // path -> the text Blip last saw
    this.lastBuild = null;          // the last compile's result
    this.ran = false;
    this.todo = todo;
    this.world.begin();
    this.models.made = 0;           // models and pictures saved
    this.notes.begin({ todo, words, pictures });
  }

  // What a call does, for Blip's bubble.
  describe(call) {
    const input = call.input || {};
    switch (call.name) {
      case "str_replace_based_edit_tool": {
        const path = typeof input.path === "string" ? input.path.replace(/^\.?\//, "") : "";
        if (input.command === "view") return path && path !== "." ? `Reading ${path}` : "Looking at the files";
        if (input.command === "create") return `Writing ${path}`;
        return `Changing ${path}`;
      }
      case "compile": return typeof input.file === "string" && input.file ? `Compiling ${input.file}` : "Compiling";
      case "run_program": {
        const seconds = Math.max(1, Math.round((input.frames || 60) / 60));
        return `Running ${typeof input.file === "string" && input.file ? input.file : "it"} for ${plural(seconds, "second")}`;
      }
      case "set_main_file": return `Making ${input.path || "a file"} the one Run starts`;
      case "online_test": return `Starting an online test of ${input.world || input.file || "the program"}`;
      case "lookup_command": return `Looking up ${input.name || "a command"}`;
      case "search_help": return `Searching the help for "${input.query || ""}"`;
      case "read_help": return `Reading the help: ${input.path || ""}`;
      case "save_todo": return "Writing the TODO note";
      case "draw_icon": return `Drawing ${typeof input.path === "string" && input.path ? input.path : "an icon"}`;
      default: return MODEL_TOOLS.has(call.name) ? this.models.describe(call) : this.world.describe(call);
    }
  }

  // Runs a tool call. Returns its tool_result block.
  async run(call, signal) {
    const result = (content, isError = false) => ({ type: "tool_result", tool_use_id: call.id, content,
      ...(isError ? { is_error: true } : {}) });
    try {
      const input = call.input && typeof call.input === "object" ? call.input : {};
      if (this.todo && !TODO_TOOLS.has(call.name) &&
          !(call.name === "str_replace_based_edit_tool" && input.command === "view")) return result(TODO_REFUSED, true);
      let content;
      switch (call.name) {
        case "str_replace_based_edit_tool": content = await this.editor(input); break;
        case "save_todo": content = await this.notes.save(input); break;
        case "compile": content = await this.compile(input); break;
        case "set_main_file": content = await this.setMain(input); break;
        case "run_program": content = await this.runProgram(input, signal); break;
        case "online_test": content = await this.onlineTest(input); break;
        case "lookup_command": content = await this.lookupCommand(input); break;
        case "search_help": content = await this.searchHelp(input); break;
        case "read_help": content = await this.readHelp(input); break;
        case "draw_icon": content = await this.drawIcon(input); break;
        default:
          content = MODEL_TOOLS.has(call.name) ? await this.models.run(call.name, input, signal) :
            await this.world.run(call.name, input, signal);
      }
      return result(content);
    } catch (error) {
      const expected = error instanceof ToolError || error instanceof WorldToolError || error instanceof ModelToolError ||
        error instanceof TodoError || error instanceof IconError;
      if (!expected) console.error(error);
      return result(expected ? error.message : `The tool failed: ${error.message || error}`, true);
    }
  }

  // ------------------------------------------------------------- the files

  // A path in the project, from one Claude gave: the project's own spelling
  // when a file has it in another case, as Include finds them.
  path(input) {
    if (typeof input !== "string" || !input.trim()) throw new ToolError("Give a path, such as main.bb.");
    let text = input.trim();
    if (/\\|^[a-z]+:/i.test(text)) throw new ToolError(`${text} is not a path in the project: use forward slashes, as in levels/one.bb.`);
    text = text.replace(/^(\.\/)+/, "").replace(/^\/+/, "");
    if (text === "" || text === ".") return "";
    const parts = splitPath(text);
    if (!parts.length || parts.some((part) => part === "." || part === "..")) {
      throw new ToolError(`${input} is not a path in the project.`);
    }
    const path = normalisePath(text);
    const project = this.ide.project();
    if (project.has(path)) return path;
    const lower = path.toLowerCase();
    return project.paths().find((other) => other.toLowerCase() === lower) || path;
  }

  // A text file's text, as the editor has it, with \n line ends.
  async text(path) {
    const { editor } = this.ide;
    const open = editor.textOf(path);
    if (open !== undefined) return open;
    const project = this.ide.project();
    editor.ensure(path, await project.text(path));
    return editor.textOf(path);
  }

  record(path, before, after) {
    const change = this.changes.get(path);
    if (change) change.after = after;
    else this.changes.set(path, { path, before, after });
    this.seen.set(path, after);
  }

  async editor(input) {
    const project = this.ide.project();
    if (!project) throw new ToolError("No program is open.");
    const command = input.command;
    if (command === "view") return this.view(input);
    const path = this.path(input.path);
    if (!path) throw new ToolError("Give a file's path.");
    if (!this.ide.isText(path)) {
      throw new ToolError(`${path} is not a kind of file Blip can write: only text files, such as .bb, .txt or .json.`);
    }
    const { editor } = this.ide;
    if (command === "create") {
      if (typeof input.file_text !== "string") throw new ToolError("Give the file's text as file_text.");
      const text = input.file_text.replace(/\r\n?/g, "\n");
      if (new Blob([text]).size > FILE_BYTES) throw new ToolError("That file is too large: Blip writes files of up to 1 MB.");
      if (project.has(path)) {
        const before = await this.text(path);
        if (this.seen.get(path) !== before) {
          throw new ToolError(`${path} already exists, and it changed since you last viewed it, or you have not ` +
            "viewed it yet. View it first, or change it with str_replace.");
        }
        const after = editor.edit(path, before, { from: 0, to: before.length, insert: text });
        await this.settled();
        this.record(path, this.changes.has(path) ? this.changes.get(path).before : before, after);
        return `Replaced ${path}: ${plural(lines(after).length, "line")}.`;
      }
      try {
        await project.write(path, text);
      } catch (error) {
        throw new ToolError(error.message || String(error));
      }
      editor.ensure(path, text);
      this.record(path, null, text);
      this.ide.refreshFiles();
      return `Created ${path}: ${plural(lines(text).length, "line")}.`;
    }
    if (!project.has(path)) throw new ToolError(`There is no file ${path}. View the project's files to see their names.`);
    const before = await this.text(path);
    const original = this.changes.has(path) ? this.changes.get(path).before : before;
    if (command === "str_replace") {
      if (typeof input.old_str !== "string" || !input.old_str) throw new ToolError("Give the text to replace as old_str.");
      const oldText = input.old_str.replace(/\r\n?/g, "\n");
      const newText = typeof input.new_str === "string" ? input.new_str.replace(/\r\n?/g, "\n") : "";
      const first = before.indexOf(oldText);
      if (first < 0) {
        throw new ToolError(`old_str is not in ${path}, exactly as given. The user may have changed the file: view it ` +
          "again, and copy the text exactly, with its spaces and tabs.");
      }
      const rows = [];
      for (let at = first; at >= 0; at = before.indexOf(oldText, at + 1)) rows.push(lines(before.slice(0, at)).length);
      if (rows.length > 1) {
        throw new ToolError(`old_str appears ${rows.length} times in ${path} (from rows ${rows.join(", ")}). ` +
          "Include more of the lines around it, so that it appears once.");
      }
      const after = editor.edit(path, before, { from: first, to: first + oldText.length, insert: newText });
      await this.settled();
      this.record(path, original, after);
      const row = lines(before.slice(0, first)).length;
      const shown = lines(after).slice(Math.max(0, row - 4), row - 1 + lines(newText).length + 3);
      return `Changed ${path}. Its lines around the change now read:\n${numbered(shown.join("\n"), Math.max(1, row - 3))}`;
    }
    if (command === "insert") {
      const count = lines(before).length;
      const after_line = Number(input.insert_line);
      if (!Number.isInteger(after_line) || after_line < 0 || after_line > count) {
        throw new ToolError(`insert_line must be a row from 0 (the start) to ${count} (the end of ${path}).`);
      }
      if (typeof input.insert_text !== "string") throw new ToolError("Give the text to insert as insert_text.");
      const text = input.insert_text.replace(/\r\n?/g, "\n").replace(/\n$/, "");
      // The start of the row after after_line.
      let at = 0;
      for (let row = 0; row < after_line && row < count - 1; ++row) at = before.indexOf("\n", at) + 1;
      let after;
      if (after_line === 0) after = editor.edit(path, before, { from: 0, to: 0, insert: `${text}\n` });
      else if (after_line === count) after = editor.edit(path, before, { from: before.length, to: before.length, insert: `\n${text}` });
      else after = editor.edit(path, before, { from: at, to: at, insert: `${text}\n` });
      await this.settled();
      this.record(path, original, after);
      return `Inserted ${plural(lines(text).length, "line")} into ${path} after row ${after_line}.`;
    }
    throw new ToolError(`The editor has no ${command} command: use view, create, str_replace or insert.`);
  }

  // Waits until an edit is in the IDE, where the editor writes them in the
  // background (the desktop IDE), so that a change the IDE refuses, such as
  // in the installed examples, is this call's error rather than a silent loss.
  async settled() {
    if (!this.ide.editor.settle) return;
    try {
      await this.ide.editor.settle();
    } catch (error) {
      throw new ToolError(error.message || String(error));
    }
  }

  async view(input) {
    const project = this.ide.project();
    const path = this.path(input.path === undefined ? "" : input.path);
    const folder = path === "" ? "" : `${path}/`;
    if (!project.has(path)) {
      const inside = project.paths().filter((name) => name.startsWith(folder));
      if (!inside.length) throw new ToolError(`There is no file or folder ${path} in the project.`);
      const entries = await Promise.all(inside.map(async (name) => {
        const blob = project.blob(name);
        let kind;
        if (this.ide.isText(name)) {
          const text = await this.text(name);
          kind = plural(lines(text).length, "line");
        } else {
          kind = `${/\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name) ? "picture" : /\.(wav|ogg|mp3|flac)$/i.test(name) ?
            "sound" : "file"}, ${blob.size < 1024 ? `${blob.size} bytes` : `${Math.round(blob.size / 1024)} KB`}`;
        }
        return `${name} (${kind}${name === project.main ? ", the file Run starts" : ""})`;
      }));
      return `${path ? `The folder ${path}` : `The project ${project.name}`} has ${plural(entries.length, "file")}:\n` +
        entries.join("\n");
    }
    if (!this.ide.isText(path)) {
      const size = project.blob(path).size;
      return `${path} is not a text file (${size < 1024 ? `${size} bytes` : `${Math.round(size / 1024)} KB`}), so it ` +
        "cannot be shown as text.";
    }
    const text = await this.text(path);
    this.seen.set(path, text);
    const all = lines(text);
    let first = 1, last = all.length;
    if (input.view_range !== undefined) {
      const range = input.view_range;
      if (!Array.isArray(range) || range.length !== 2 || !Number.isInteger(range[0]) || !Number.isInteger(range[1])) {
        throw new ToolError("view_range is two row numbers, such as [10, 40], or [10, -1] for the rest of the file.");
      }
      first = Math.max(1, range[0]);
      last = range[1] === -1 ? all.length : Math.min(all.length, range[1]);
      if (first > last) throw new ToolError(`${path} has ${plural(all.length, "row")}.`);
    }
    const shown = numbered(all.slice(first - 1, last).join("\n"), first);
    return cut(shown, VIEW_CHARACTERS, `the file goes on: view a range of rows, such as [${first + 400}, -1], to see more`);
  }

  // draw_icon (icons.js): an icon he draws himself, in the World Editor's
  // icon style, saved as a PNG. One he drew before at its path is drawn
  // again there; any other file there is kept, and the icon goes beside it.
  // It comes back to him, to see.
  async drawIcon(input) {
    const project = this.ide.project();
    if (!project) throw new ToolError("No program is open.");
    const where = "path is where the icon goes: a .png file, such as recipes/crater/icon.png.";
    if (typeof input.path !== "string" || !/\.png$/i.test(input.path.trim())) throw new ToolError(where);
    const wanted = this.path(input.path);
    const shapes = iconShapes(input.shapes);
    const bytes = await drawIcon(shapes, iconView(input.view));
    let path = wanted, again = false;
    if (project.has(wanted)) {
      again = isBlipIcon(await project.bytes(wanted));
      if (!again) path = freePath(project, wanted);
    }
    await this.ide.addFile(path, bytes);
    if (this.ide.worldEditor()) await this.ide.putToolFile(path, bytes);
    ++this.models.made;
    const parts = [`Drew ${path} from ${plural(shapes.length, "shape")}, ${ICON_SIZE} x ${ICON_SIZE} pixels` +
      `${again ? ", in place of the icon you drew there before" : ""}.`];
    if (path !== wanted) parts.push(`${wanted} is not an icon you drew, so it is kept: name ${path} where you meant ${wanted}.`);
    parts.push("The tile of a recipe whose icon it is shows it within a second. This is the icon:");
    return [{ type: "text", text: parts.join(" ") },
      { type: "image", source: { type: "base64", media_type: "image/png", data: paddedBase64(bytes) } }];
  }

  // -------------------------------------------------- compiling and running

  // A compile's result as text: no errors, or each error with its line.
  async errorsText(result) {
    if (result.ok) return "No errors.";
    if (!result.errors || !result.errors.length) return `The program could not be compiled:\n${result.output || ""}`.trim();
    const shown = [];
    for (const error of result.errors.slice(0, 20)) {
      let line = "";
      const path = this.ide.projectPath(error.file);
      if (path && this.ide.isText(path)) {
        const text = await this.text(path);
        line = (lines(text)[error.row - 1] || "").trim();
      }
      shown.push(`${error.file}:${error.row}:${error.col}: ${error.message}${line ? `\n    ${error.row} | ${line}` : ""}`);
    }
    return `${plural(result.errors.length, "error")}:\n${shown.join("\n")}`;
  }

  // A program of the project, by its path: a .bb file it has. null when none
  // is given, for the main file.
  program(input) {
    if (input === undefined || input === null || input === "") return null;
    const path = this.path(input);
    if (!/\.bb$/i.test(path)) throw new ToolError(`${path} is not a program: give a .bb file.`);
    if (!this.ide.project().has(path)) throw new ToolError(`There is no file ${path}. View the project's files to see their names.`);
    return path;
  }

  async compile(input = {}) {
    const result = await this.ide.compile(this.program(input.file));
    this.lastBuild = result;
    return this.errorsText(result);
  }

  // Makes a program the main file, the one Run starts, keeping the one it
  // was before this request for the change card's Undo.
  async setMain(input) {
    const project = this.ide.project();
    if (!project) throw new ToolError("No program is open.");
    const path = this.program(input.path);
    if (!path) throw new ToolError("Give the .bb file Run should start, as path.");
    const before = project.main || null;
    if (before === path) return `${path} is already the file Run starts.`;
    await this.ide.setMain(path);
    if (project.main !== path) throw new ToolError(`${path} could not be made the file Run starts.`);
    this.main = { before: this.main ? this.main.before : before, after: path };
    return `Run now starts ${path}${before ? `, not ${before}` : ""}. Tell the user so in your reply.`;
  }

  // The online test (Program > Run online test): the IDE runs the program,
  // or the World Editor's Player on a world, as a server room on the user's
  // computer, with no window, and joins it: the desktop IDEs in up to four
  // windows of the user's own, the online IDE (browser) once, in its program
  // pane. He can't see either.
  async onlineTest(input) {
    const players = input.players === undefined ? 1 : input.players;
    if (!Number.isInteger(players) || players < 1 || players > 4) throw new ToolError("players is a whole number from 1 to 4.");
    if (input.bots !== undefined && (!Number.isInteger(input.bots) || input.bots < 0 || input.bots > 128)) {
      throw new ToolError("bots is a whole number from 0 to 128, or left out for the program's own.");
    }
    if (input.network !== undefined && !NETWORKS.includes(input.network)) {
      throw new ToolError(`network is one of ${NETWORKS.join(", ")}.`);
    }
    if (input.file && input.world) throw new ToolError("Give file or world, not both.");
    const request = { players };
    if (input.bots !== undefined) request.bots = input.bots;
    if (input.network) request.network = input.network;
    if (input.file) request.path = this.program(input.file);
    if (input.world) {
      const world = this.path(input.world);
      if (!/\.world$/i.test(world) || !this.ide.project().has(world)) throw new ToolError(`There is no world ${world} in the project.`);
      request.world = world;
    }
    if (!this.ide.onlineTest) return "This IDE can't run an online test. Tell the user so.";
    let result;
    try {
      result = await this.ide.onlineTest(request);
    } catch (error) {
      throw new ToolError(`The online test did not start: ${error.message || error}.`);
    }
    const bots = result.bots < 0 ? "the program's own bots" : result.bots === 0 ? "no bots" :
      result.bots >= 128 ? "bots filling the room" : `bots keeping ${result.bots} players in the room`;
    const network = result.network === "none" ? "no bad network" : `a ${result.network} network`;
    const what = request.world ? `${request.world}, played by the World Editor's Player,` : result.program;
    if (result.browser) {
      return `The online test started: ${what} runs as a server room in a frame of this browser that no one sees, ` +
        `with ${bots} and ${network}, and the user joins it in the program pane once its room is open. ` +
        (players > 1 ? "The online IDE runs one player; the desktop IDEs run up to four on one computer. " : "") +
        "You can't see it: tell the user what to try, that the console shows what the server prints after Server:, " +
        "and that ending the program ends the test.";
    }
    return `The online test started: ${what} runs as a server room on the user's computer, with ${bots} and ` +
      `${network}, and ${plural(result.players, "window")} of the user's ${result.players === 1 ? "joins" : "join"} it ` +
      "once its room is open. You can't see them: tell the user what to try, that the console shows what the server " +
      "prints after Server:, and that closing the first window ends the test.";
  }

  async runProgram(input, signal) {
    const frames = input.frames === undefined ? 60 : Number(input.frames);
    if (!Number.isInteger(frames) || frames < 1 || frames > 600) throw new ToolError("frames is a whole number from 1 to 600.");
    const keys = input.keys === undefined ? [] : input.keys;
    if (!Array.isArray(keys) || keys.length > 64 || keys.some((key) => !key || !Number.isInteger(key.frame) ||
        key.frame < 1 || key.frame > 600 || !Number.isInteger(key.scancode) || key.scancode < 1 || key.scancode > 255)) {
      throw new ToolError("keys is a list of { frame, scancode }, frames from 1 to 600 and scan codes from 1 to 255.");
    }
    const file = this.program(input.file);
    const outcome = await this.ide.checkRun({ frames, keys, signal, file });
    if (outcome.status === "compile-error") {
      this.lastBuild = { ok: false, errors: outcome.errors, output: outcome.output };
      return `The program did not run: it has ${await this.errorsText(this.lastBuild)}`;
    }
    this.lastBuild = { ok: true, errors: [] };
    if (outcome.status === "desktop") {
      return "It compiled with no errors. In the desktop IDE you don't run programs yourself: the user runs them, in a " +
        "window of their own, where you can't see them. Tell the user it is ready to try with Run (F5), and what to look for.";
    }
    if (outcome.status === "editor-open") {
      return "It compiled with no errors, but did not run, as the World Editor is open.\n\n" +
        "A run would take the editor's place and close it, losing any changes the user has not saved there. " +
        "Tell the user the program is ready to try once they have saved their world and closed the World Editor, " +
        "and do not run it again before then.";
    }
    this.ran = true;
    const parts = [];
    switch (outcome.status) {
      case "captured": parts.push(`It ran ${plural(frames, "frame")}, and was stopped there. The picture is its last frame.`); break;
      case "ended": parts.push(outcome.frames ? "It ended by itself before that frame." : "It ended by itself."); break;
      case "error": parts.push("It stopped with a runtime error."); break;
      case "not-responding": parts.push("It stopped responding (a loop with no Flip, or a wait that never ends), so it was stopped."); break;
      case "timeout": parts.push(`It did not reach frame ${frames} in time${outcome.drew ? "" : ", and drew nothing"}: it may wait ` +
        "for a key before its first Flip, or run slowly. It was stopped."); break;
      case "hidden": parts.push("The IDE's tab was hidden, so the program could not draw. Try again when the user is looking."); break;
      case "cannot-run": parts.push("This browser cannot run programs, so it was only compiled: it has no errors."); break;
      case "stopped": parts.push("The run was stopped."); break;
      default: parts.push(`It ended (${outcome.status}).`);
    }
    // The last frame, made smaller. A screen wider than the picture is named,
    // so that he compares it with a user's screenshot in proportion.
    let picture = null;
    if (outcome.capture) {
      try {
        picture = await smallPicture(outcome.capture);
      } catch (error) {
        console.error(error);
      }
    }
    const screen = picture ? pngSize(outcome.capture) : null;
    const shown = screen ? smallSize(screen.width, screen.height) : null;
    if (shown && shown.width < screen.width) {
      parts.push(`The picture shows its ${screen.width} x ${screen.height} screen at ${shown.width} x ${shown.height}.`);
    }
    if (outcome.noGpu) parts.push("This browser has no WebGPU, so 3D graphics are not shown.");
    if (outcome.message && outcome.status === "error") {
      const where = outcome.location ? ` at ${outcome.location.file}, row ${outcome.location.line}` : "";
      parts.push(`Runtime error${where}: ${outcome.message}`);
    }
    const printed = (outcome.output || []).slice(-OUTPUT_LINES).join("\n");
    parts.push(printed ? `It printed:\n${cut(printed, OUTPUT_CHARACTERS, "and more")}` : "It printed nothing.");
    const content = [{ type: "text", text: parts.join("\n\n") }];
    if (picture) content.push({ type: "image", source: { type: "base64", media_type: picture.type, data: picture.data } });
    return content;
  }

  // ------------------------------------------------------------------ help

  async lookupCommand(input) {
    if (typeof input.name !== "string" || !input.name.trim()) throw new ToolError("Give a command's name.");
    const name = input.name.trim().replace(/[%#$&@]$/, "");
    const command = this.ide.commands.get(name.toLowerCase());
    if (!command) {
      const lower = name.toLowerCase();
      // An official module's function, type or other name.
      const named = (await this.loadSearchIndex()).filter((entry) => entry.m && entry.t.toLowerCase() === lower);
      if (named.length) {
        return named.map((entry) => {
          const page = entry.p.replace(/#.*$/, "");
          return `${entry.t} is not one of the runtime's commands: it is a ${entry.k} of the official module ` +
            `${entry.m}, which a program imports at its top, in Extended mode (Import ${entry.m}), and uses with ` +
            `the module's name in front.\n\n${entry.w}\n\nThe module's page, with its Import line, a guide and a ` +
            `complete program, is ${page}; ${entry.p} reads from this ${entry.k}'s part of the reference.`;
        }).join("\n\n");
      }
      const similar = [...this.ide.commands.values()].filter((other) => other.name.toLowerCase().includes(lower) ||
        lower.includes(other.name.toLowerCase().slice(0, Math.max(4, lower.length - 2)))).slice(0, 12)
        .map((other) => other.name);
      throw new ToolError(`There is no command ${name}.${similar.length ? ` Similar commands: ${similar.join(", ")}.` : ""}`);
    }
    const parts = [`${commandSignature(command)}\n${command.extended ? "An Extended command: Extended mode only." :
      "An Original command: both modes have it."}`];
    const page = this.ide.help.pageFor(command.name);
    if (page) {
      const response = await fetch(versioned(page));
      if (response.ok) {
        const html = await response.text();
        const example = /href="?([^"\s>]+\.bb)"?/i.exec(html);
        if (example) {
          const url = new URL(example[1], page);
          const base = new URL("commands/", this.ide.help.base);
          if (url.href.startsWith(base.href)) parts.push(`Example program: commands/${url.href.slice(base.href.length)}`);
        }
        parts.push(cut(htmlText(html), COMMAND_PAGE_CHARACTERS, "the page goes on: read_help reads all of it"));
      }
    }
    return parts.join("\n\n");
  }

  // The help's search index (help/search.json, from web/ide/tools/blip_pack.mjs).
  async loadSearchIndex() {
    if (!this.searchIndex) {
      const response = await fetch(versioned(new URL("search.json", this.ide.help.base)));
      this.searchIndex = response.ok ? await response.json() : [];
    }
    return this.searchIndex;
  }

  async searchHelp(input) {
    if (typeof input.query !== "string" || !input.query.trim()) throw new ToolError("Give a few words to search for.");
    const words = input.query.toLowerCase().split(/[^a-z0-9]+/).filter((word) => word.length > 1);
    if (!words.length) throw new ToolError("Give a few words to search for.");
    const scored = [];
    for (const entry of await this.loadSearchIndex()) {
      const title = entry.t.toLowerCase(), more = (entry.w || "").toLowerCase();
      let score = 0;
      for (const word of words) {
        if (title === word) score += 12;
        else if (title.split(/[^a-z0-9]+/).includes(word)) score += 6;
        else if (title.includes(word)) score += 3;
        if (more.includes(word)) score += 1;
      }
      if (score) scored.push([score + (entry.k === "command" ? 0 : 0.5), entry]);
    }
    scored.sort((a, b) => b[0] - a[0]);
    if (!scored.length) return `Nothing in the help matches "${input.query}". Try other words, or a command's name.`;
    return scored.slice(0, 12).map(([, entry]) =>
      `- ${entry.t} (${entry.m ? `${entry.k} of the official module ${entry.m}` : entry.k}): ${entry.p}`).join("\n");
  }

  async readHelp(input) {
    if (typeof input.path !== "string" || !input.path.trim()) throw new ToolError("Give the page's path in the help.");
    let path = input.path.trim().replace(/\\/g, "/").replace(/^\/+/, "").replace(/^help\//, "");
    // A part of the page, such as modules/tweening.html#reference.
    const hash = path.indexOf("#");
    let part = hash < 0 ? "" : path.slice(hash + 1);
    try {
      part = decodeURIComponent(part);
    } catch {
      // A % of its own, kept as it is.
    }
    if (hash >= 0) path = path.slice(0, hash);
    if (path.split("/").some((part) => part === ".." || part === ".") || /^[a-z]+:/i.test(path)) {
      throw new ToolError(`${input.path} is not a path in the help.`);
    }
    const url = new URL(path, this.ide.help.base);
    const response = await fetch(versioned(url));
    if (!response.ok) throw new ToolError(`There is no page ${path} in the help.`);
    const text = await response.text();
    if (/\.bb$/i.test(path)) {
      const example = this.ide.examples().find((entry) => `commands/${entry.path}`.toLowerCase() === path.toLowerCase());
      const media = example && example.files && example.files.length ? `\n\nIt loads: ${example.files.join(", ")}` : "";
      return `${cut(text.replace(/\r\n/g, "\n"), PAGE_CHARACTERS, "the program goes on")}${media}`;
    }
    const page = htmlText(text, part);
    const parts = helpParts(text);
    if (page === null) {
      throw new ToolError(`${path} has no part #${part}.${parts.length ?
        ` Its parts: ${parts.map((each) => `${path}#${each.id}`).join(", ")}.` : ""}`);
    }
    if (page.length <= PAGE_CHARACTERS) return page;
    // Where to read on from: the page's parts after the cut.
    const rest = page.slice(PAGE_CHARACTERS);
    const later = parts.filter((each) => rest.includes(`\n${each.heading}\n`)).map((each) => `${path}#${each.id}`);
    const more = later.length > HELP_PARTS ? `, and ${plural(later.length - HELP_PARTS, "more part")}` : "";
    return cut(page, PAGE_CHARACTERS, later.length ?
      `the page goes on: read_help reads on from ${later.slice(0, HELP_PARTS).join(", ")}${more}` : "the page goes on");
  }

  // ------------------------------------------------------------ the record

  // The request's changes: [{ path, before, after, rows }], marked in the
  // editor. The main file he set, if he did, is in main.
  finish() {
    const changes = [];
    for (const change of this.changes.values()) {
      if (change.before === change.after) continue;
      const rows = changedRows(change.before, change.after);
      this.ide.editor.markChanges(change.path, rows);
      changes.push({ ...change, rows });
    }
    return changes;
  }
}

export class ToolError extends Error {}

// Bytes as standard base64, padded with =, as the API takes pictures.
export function paddedBase64(bytes) {
  let binary = "";
  for (let at = 0; at < bytes.length; at += 0x8000) binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  return btoa(binary);
}

// The size smallPicture makes a picture of width x height.
function smallSize(width, height, most = CAPTURE_WIDTH) {
  const scale = Math.min(1, most / width);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

// A PNG's size in pixels, from its header; null for another file.
function pngSize(bytes) {
  const header = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (header.length < 24 || header[0] !== 0x89 || header[1] !== 0x50 || header[2] !== 0x4e || header[3] !== 0x47) return null;
  const word = (at) => (header[at] << 24 | header[at + 1] << 16 | header[at + 2] << 8 | header[at + 3]) >>> 0;
  return { width: word(16), height: word(20) };
}

// A captured frame or picture, at most width wide, as the API takes it:
// { type, data }, data being base64. A PNG, unless it is large and a JPEG is
// smaller, as it is for a world's textured ground: the whole chat goes to
// the server at each step, and a chat holds many pictures.
export async function smallPicture(bytes, width = CAPTURE_WIDTH) {
  const bitmap = await createImageBitmap(new Blob([bytes], { type: "image/png" }));
  const size = smallSize(bitmap.width, bitmap.height, width);
  const canvas = new OffscreenCanvas(size.width, size.height);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  let blob = await canvas.convertToBlob({ type: "image/png" });
  if (blob.size > PNG_BYTES) {
    const jpeg = await canvas.convertToBlob({ type: "image/jpeg", quality: 0.85 });
    if (jpeg.size < blob.size) blob = jpeg;
  }
  return { type: blob.type, data: paddedBase64(new Uint8Array(await blob.arrayBuffer())) };
}
